"use client";

import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import {
  BookOpen,
  FileText,
  Loader2,
  MessageSquare,
  Receipt,
  ReceiptText,
  Search,
  UserSquare2,
  WalletCards,
} from "lucide-react";

import { useModalPanels } from "@/@libs/modal-panels/use-modal-panels";
import CustomerProfile from "@/components/customers/customer-profile-sidebar/customer-profile.sidebar-registry";
import ViewEnquiry from "@/components/enquiries/view-enquiry-modal/view-enquiry.modal-registry";
import ViewPaymentReceipt from "@/components/bookings/booking-view/view-payment-receipt-modal/view-payment-receipt-modal.registry";
import ViewSupplierPaymentReceipt from "@/components/bookings/booking-products/modals/view-supplier-payment-receipt-modal/view-supplier-payment-receipt-modal.registry";
import type {
  CustomerPaymentReceiptListRow,
  SupplierBillPaymentReceiptListRow,
} from "@sdk/accounting/accounting-list-report.models";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useDebouncedValue } from "@/utils/use-debounced-value";

import { useGlobalSearchContext } from "./global-search-context";
import type {
  GlobalSearchResult,
  GlobalSearchResultType,
} from "./global-search.types";
import { useGlobalSearch } from "./use-global-search";

const TYPE_ICONS: Record<GlobalSearchResultType, typeof Search> = {
  customer: UserSquare2,
  enquiry: MessageSquare,
  quote: FileText,
  booking: BookOpen,
  "customer-invoice": ReceiptText,
  "supplier-bill": Receipt,
  "customer-payment-receipt": WalletCards,
  "supplier-bill-payment-receipt": WalletCards,
};

export function GlobalCommandPalette() {
  const { open, setOpen } = useGlobalSearchContext();
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchText] = useDebouncedValue(inputValue, 350);
  const { groups, isLoading, hasResults } = useGlobalSearch(debouncedSearchText);
  const navigate = useNavigate();
  const { changePanelState, triggerTempModal } = useModalPanels();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        setInputValue("");
      }
    },
    [setOpen]
  );

  const handleSelect = useCallback(
    (result: GlobalSearchResult) => {
      handleOpenChange(false);

      switch (result.type) {
        case "customer":
          changePanelState(CustomerProfile, true, { customerId: result.id });
          break;
        case "enquiry":
          changePanelState(ViewEnquiry, true, { enquiryId: result.id });
          break;
        case "quote":
          navigate(`/quotes/${result.id}`);
          break;
        case "booking":
          navigate(`/bookings/${result.id}`);
          break;
        case "customer-invoice":
          if (result.bookingId) {
            navigate(`/bookings/${result.bookingId}`);
          } else {
            navigate("/accounting/invoices");
          }
          break;
        case "supplier-bill":
          if (result.bookingId) {
            navigate(`/bookings/${result.bookingId}`);
          } else {
            navigate("/accounting/bills");
          }
          break;
        case "customer-payment-receipt": {
          const receipt = result.raw as CustomerPaymentReceiptListRow | undefined;
          const bookingId = result.bookingId;
          if (bookingId && receipt?.id) {
            triggerTempModal(ViewPaymentReceipt, {
              bookingId,
              receipt,
              accountingContext: true,
            });
          } else if (bookingId) {
            navigate(`/bookings/${bookingId}`);
          } else {
            navigate("/accounting/customer-invoice-payments");
          }
          break;
        }
        case "supplier-bill-payment-receipt": {
          const receipt =
            result.raw as SupplierBillPaymentReceiptListRow | undefined;
          const bookingId = result.bookingId;
          const productId = result.productId ?? receipt?.productId;
          if (bookingId && productId && receipt?.id) {
            triggerTempModal(ViewSupplierPaymentReceipt, {
              bookingId,
              productId,
              receipt,
              accountingContext: true,
            });
          } else if (bookingId) {
            navigate(`/bookings/${bookingId}`);
          } else {
            navigate("/accounting/supplier-bill-payments");
          }
          break;
        }
      }
    },
    [changePanelState, handleOpenChange, navigate, triggerTempModal]
  );

  const trimmedSearch = debouncedSearchText.trim();
  const showEmptyState = trimmedSearch.length > 0 && !isLoading && !hasResults;
  const isDebouncing = inputValue !== debouncedSearchText;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="overflow-hidden p-0 shadow-2xl sm:max-w-2xl gap-0">
        <Command
          shouldFilter={false}
          className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        >
          <CommandInput
            placeholder="Search Workspace"
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList className="max-h-[420px]">
            {isLoading || isDebouncing ? (
              <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            ) : null}

            {!isLoading && !isDebouncing && !trimmedSearch ? (
              <CommandEmpty className="px-6 py-12">
                <div className="flex flex-col items-center gap-1 text-center text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">
                    Search your workspace
                  </p>
                  <p className="max-w-sm text-xs leading-relaxed">
                    Find customers, enquiries, quotes, bookings, invoices, and
                    payments.
                  </p>
                </div>
              </CommandEmpty>
            ) : null}

            {showEmptyState && !isDebouncing ? (
              <CommandEmpty className="px-6 py-12 text-center text-sm text-muted-foreground">
                No results found for &ldquo;{trimmedSearch}&rdquo;.
              </CommandEmpty>
            ) : null}

            {!isLoading &&
              !isDebouncing &&
              groups.map((group, groupIndex) => (
                <div key={group.type}>
                  {groupIndex > 0 ? <CommandSeparator /> : null}
                  <CommandGroup heading={group.label}>
                    {group.results.map((result) => {
                      const Icon = TYPE_ICONS[result.type];
                      return (
                        <CommandItem
                          key={`${result.type}-${result.id}`}
                          value={`${result.type}-${result.id}`}
                          onSelect={() => handleSelect(result)}
                          className="cursor-pointer items-start gap-3 py-3"
                        >
                          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/50">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <span className="truncate font-medium">
                                  {result.title}
                                </span>
                                {result.badge ? (
                                  <Badge
                                    variant="secondary"
                                    className="shrink-0 text-[10px] font-normal"
                                  >
                                    {result.badge}
                                  </Badge>
                                ) : null}
                              </div>
                              {result.trailing ? (
                                <span className="shrink-0 text-xs font-medium tabular-nums text-foreground">
                                  {result.trailing}
                                </span>
                              ) : null}
                            </div>
                            {result.subtitle ? (
                              <span className="truncate text-xs text-muted-foreground">
                                {result.subtitle}
                              </span>
                            ) : null}
                            {result.details && result.details.length > 0 ? (
                              <span className="truncate text-xs text-muted-foreground/80">
                                {result.details.join(" · ")}
                              </span>
                            ) : null}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </div>
              ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
